import { kpiWord, settingFor } from '../../audit/budgets';
import { kpiHint, kpiNumber, type KpiContext } from '../../audit/kpiContext';
import type { AnswerValue, Question } from '../../audit/template';
import { easternIsoDay } from '../../data/taskRules';
import { formatDay } from '../dates';

const BAND_WORDS = { green: 'meets the %s', amber: 'a little off the %s', red: 'well off the %s' } as const;

/**
 * The line under a KPI box (B8, owner rulings 2026-10-25): the store's last
 * value, this month's budget (pro-rated to today for month-to-date counts)
 * with a coloured dot for the typed value, and an amber "Big change —
 * double-check" when it's far from the last visits. Information only —
 * never blocks the form.
 */
export function KpiHintLine({
  context,
  question,
  value,
  today = easternIsoDay(new Date()),
}: {
  context: KpiContext | undefined;
  question: Question;
  value: AnswerValue | undefined;
  /** Eastern yyyy-mm-dd (tests pass one). */
  today?: string;
}) {
  if (question.type !== 'number' && question.type !== 'percent') return null;
  const type = question.type;
  const typed = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const hint = kpiHint(context, question.id, type, typed, today);
  if (!hint) return null;
  const unit = type === 'percent' ? '%' : '';
  const n = (v: number) => `${kpiNumber(v)}${unit}`;
  const setting = settingFor(context?.settings, question.id, type);
  const lowerBetter = setting.dir === 'down';
  // A count has a budget, a level a goal (owner ruling 2026-10-25).
  const word = kpiWord(setting, true);
  const bandWord = (band: keyof typeof BAND_WORDS) => BAND_WORDS[band].replace('%s', word);
  return (
    <div className="kpi-hint" aria-live="polite">
      {hint.last && (
        <span>
          Last visit: <strong>{n(hint.last.value)}</strong> ({formatDay(hint.last.day)})
        </span>
      )}
      {hint.budget && (
        <span>
          {hint.band && (
            <span className={`kpi-dot kpi-${hint.band}`} role="img" aria-label={bandWord(hint.band)} title={bandWord(hint.band)} />
          )}
          {kpiWord(setting)}: <strong>{n(hint.budget.month)}</strong> this month
          {hint.budget.mtd && <> — about {n(Math.round(hint.budget.expected * 10) / 10)} by today</>}
          {lowerBetter && <span className="muted"> (lower is better)</span>}
          {/* No dot before the 5th — say why, or a count far off looks unchecked (UI review 3, D5). */}
          {hint.budget.mtd && !hint.band && typed !== null && <span className="muted"> · <span className="nowrap">too early in the month to judge</span></span>}
        </span>
      )}
      {hint.big && (
        <span className="kpi-big" role="note">
          ⚠{' '}
          {hint.big.mtd
            ? `That’s on pace for about ${n(Math.round(hint.big.now))} this month; the last visits were on pace for about ${n(Math.round(hint.big.average))} — double‑check.`
            : `Big change from the store’s last visits (average ${n(Math.round(hint.big.average * 10) / 10)}) — double‑check.`}
        </span>
      )}
    </div>
  );
}
